import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { useSocialStatus } from './SocialStatusUpdater';
import ConfirmationPopup from './confirm-popup';

const GlitchText = ({ text, className }) => (
  <span className={`relative inline-block ${className} text-purple-500 animate-pulse`}>
    {text}
  </span>
);

const renderWebsite = (website) => {
  try {
    const parsedUrl = new URL(website);
    return (
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className="text-purple-500 hover:underline"
      >
        🌐 {parsedUrl.hostname}
      </a>
    );
  } catch (e) {
    return <span className="text-slate-300">{website}</span>;
  }
};

const UserProfile = () => {
  const { username } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState('Profile');
  const [isEditing, setIsEditing] = useState(false);
  const [tempProfile, setTempProfile] = useState({});
  const [notification, setNotification] = useState(null);
  const [bannerSrc, setBannerSrc] = useState(null);
  const [bannerFailed, setBannerFailed] = useState({ gif: false, png: false });
  const navigate = useNavigate();

  // Get the auth token for API requests
  const token = localStorage.getItem('token');

  // Set up social status monitoring with the imported hook
  const socialSubscriptions = useMemo(() => {
    return profileData?.socials?.reduce((acc, social) => {
      if (!acc[social.platform]) {
        acc[social.platform] = [];
      }
      acc[social.platform].push(social.socialId);
      return acc;
    }, {});
  }, [profileData?.socials]);

  const {
    statusData,
    isConnecting: isStatusConnecting,
    isConnected: isStatusConnected,
    connectionError: statusConnectionError,
    reconnect: reconnectStatus
  } = useSocialStatus(socialSubscriptions, token);


  const [confirmationPopup, setConfirmationPopup] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmAction: () => {},
    icon: 'warning'
  });



  // Log statusData for debugging
  useEffect(() => {
    console.log('statusData:', statusData);
  }, [statusData]);

  const DISCORD_CLIENT_ID = '1353586308886433823';
  const DISCORD_REDIRECT_URI = `${window.location.origin}/user-profile`;
  const STEAM_REDIRECT_URI = `${window.location.origin}/user-profile`;

  const getDiscordAvatarUrl = (social) =>
    social?.platform === 'discord' && social.avatarHash
      ? `https://cdn.discordapp.com/avatars/${social.socialId}/${social.avatarHash}.png?size=128`
      : null;

  const getDiscordBannerUrl = (social) => {
    if (social?.platform !== 'discord' || !social.bannerHash) return null;
    const baseUrl = `https://cdn.discordapp.com/banners/${social.socialId}/${social.bannerHash}`;
    return `${baseUrl}.gif?size=600`;
  };

  const getWebSocketDiscordAvatar = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.avatarUrl || null;
    }
    return null;
  };

  const getWebSocketDiscordBanner = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.bannerUrl || null;
    }
    return null;
  };

  const getSteamAvatarUrl = (social) =>
    social?.platform === 'steam' && social.avatarHash
      ? `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/avatars/${social.avatarHash.substring(0, 2)}/${social.avatarHash}.jpg`
      : null;

  const getSocialAvatar = (platform, socialId, social, statusData) => {
    if (platform === 'discord' && statusData?.discord?.[socialId]?.avatar) {
      return getWebSocketDiscordAvatar('discord', socialId, statusData);
    } else if (platform === 'discord') {
      return getDiscordAvatarUrl(social);
    } else if (platform === 'steam') {
      return getSteamAvatarUrl(social);
    }
    return null;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/Profile');
        return;
      }
      try {
        const response = await fetch('https://api.4fingerstudios.com/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.status === 401) {
          navigate('/Profile');
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch profile');
        const data = await response.json();
        setProfileData(data);
        setTempProfile(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      handleDiscordCallback(code);
    } else {
      fetchProfile();
    }
  }, [navigate]);

  useEffect(() => {
    const discordSocial = profileData?.socials?.find((s) => s.platform === 'discord');
    if (discordSocial && statusData?.discord?.[discordSocial.socialId]) {
      const bannerUrl = getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData);
      setBannerSrc(bannerUrl);
      setBannerFailed({ gif: false, png: false }); // Reset banner failed state
    } else {
      setBannerSrc(null);
    }
  }, [statusData, profileData?.socials]);

  const handleBannerError = (type) => {
    if (type === 'gif' && !bannerFailed.gif) {
      setBannerFailed((prev) => ({ ...prev, gif: true }));
      const discordSocial = profileData?.socials?.find((s) => s.platform === 'discord');
      if (discordSocial && statusData?.discord?.[discordSocial.socialId]) {
        const bannerUrl = getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData);
        if (bannerUrl) {
          setBannerSrc(bannerUrl.replace('.gif', '.png')); // Attempt PNG fallback
        } else {
          setBannerSrc(null);
        }
      }
    } else if (type === 'png' && !bannerFailed.png) {
      setBannerFailed((prev) => ({ ...prev, png: true }));
      setBannerSrc(null);
    }
  };

  const handleEditChange = (e) => {
    setTempProfile({ ...tempProfile, [e.target.name]: e.target.value });
  };

  const saveProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(tempProfile),
      });
      if (!response.ok) throw new Error('Failed to update profile');
      const updatedData = await response.json();
      setProfileData(updatedData);
      setIsEditing(false);
      setNotification('Profile updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const requestPasswordReset = async () => {
    const token = localStorage.getItem('token');
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const { ip } = await ipResponse.json();
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Client-IP': `${ip}`,
        },
        body: JSON.stringify({ email: profileData.email }),
      });
      const responseText = await response.text();
      if (!response.ok) throw new Error(responseText || 'Failed to request password reset');
      setNotification('Please follow the instructions in your email to reset your password.');
      setError(null);
    } catch (err) {
      setError(err.message);
      setNotification(null);
    }
  };

  const handleDiscordConnect = () => {
    const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(DISCORD_REDIRECT_URI)}&response_type=code&scope=identify%20guilds`;
    window.location.href = discordAuthUrl;
  };

  const handleSteamConnect = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('You must be logged in to connect Steam');
      return;
    }
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/steam-login-url', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to initiate Steam connection');
      }
      const { redirectUrl } = await response.json();
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDiscordCallback = async (code) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('https://api.4fingerstudios.com/api/auth/discord-connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) throw new Error('Failed to connect Discord account');
      const updatedProfile = await response.json();
      setProfileData(updatedProfile);
      setTempProfile(updatedProfile);
      setBannerFailed({ gif: false, png: false });
      setNotification('Successfully connected Discord account');
      setLoading(false);
      window.history.replaceState({}, document.title, DISCORD_REDIRECT_URI);
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleUnlinkSocial = async (platform) => {
    setConfirmationPopup({
      isOpen: true,
      title: `Unlink ${platform.charAt(0).toUpperCase() + platform.slice(1)}`,
      message: `Are you sure you want to unlink your ${platform} account? This action cannot be undone.`,
      confirmAction: () => performUnlinkSocial(platform),
      icon: 'warning'
    });
  };

  const performUnlinkSocial = async (platform) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('You must be logged in to unlink a social account');
      return;
    }
    
    try {
      const response = await fetch(`https://api.4fingerstudios.com/api/auth/socials/${platform}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to unlink social account');
      }
      
      setProfileData((prev) => ({
        ...prev,
        socials: prev.socials.filter((s) => s.platform !== platform),
      }));
      
      setTempProfile((prev) => ({
        ...prev,
        socials: prev.socials.filter((s) => s.platform !== platform),
      }));
      
      if (platform === 'discord') {
        setBannerSrc(null);
        setBannerFailed({ gif: false, png: false });
      }
      
      setNotification(`Successfully unlinked ${platform} account`);
      setError(null);
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setNotification(null);
    }
  };

  const closeConfirmationPopup = () => {
    setConfirmationPopup(prev => ({ ...prev, isOpen: false }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.reload();
  };

  const getSocialStatusIndicator = (platform, socialId) => {
    const status = statusData?.[platform]?.[socialId];
    if (!status) return null;
    switch (platform) {
      case 'discord':
        return (
          <div className="flex items-center text-xs gap-1 mt-1">
            <div className={`w-2 h-2 rounded-full ${
              status.status === 'online' ? 'bg-green-500' :
              status.status === 'idle' ? 'bg-yellow-500' :
              status.status === 'dnd' ? 'bg-red-500' : 'bg-gray-500'
            }`}></div>
            <span className="text-slate-300">
              {status.status === 'online' ? 'Online' :
               status.status === 'idle' ? 'Idle' :
               status.status === 'dnd' ? 'Do Not Disturb' : 'Offline'}
              {status.game && ` • Playing ${status.game}`}
            </span>
          </div>
        );
      case 'steam':
        return (
          <div className="flex items-center text-xs gap-1 mt-1">
            <div className={`w-2 h-2 rounded-full ${
              status.online ? 'bg-green-500' : 'bg-gray-500'
            }`}></div>
            <span className="text-slate-300">
              {status.online ? (
                status.ingame ? `In game: ${status.game}` : 'Online'
              ) : 'Offline'}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const tabContentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: 20, transition: { duration: 0.3 } },
  };

  const tabs = ['Profile', 'Stats', 'Settings', 'Social'];
  const discordSocial = profileData?.socials?.find((s) => s.platform === 'discord');
  const steamSocial = profileData?.socials?.find((s) => s.platform === 'steam');
  const profileImage = isEditing
    ? tempProfile.profileImageUrl || 'https://cdn.4fingerstudios.com/assets/images/istockphoto-518552551-612x612.jpg'
    : discordSocial && statusData?.discord?.[discordSocial.socialId]
    ? getWebSocketDiscordAvatar('discord', discordSocial.socialId, statusData)
    : steamSocial
    ? getSteamAvatarUrl(steamSocial)
    : profileData?.profileImageUrl || 'https://cdn.4fingerstudios.com/assets/images/istockphoto-518552551-612x612.jpg';

  return (
    <motion.div
      className="flex flex-col min-h-screen text-slate-300 font-mono overscroll-none relative"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="fixed inset-0 z-[-1]">
        {bannerSrc && !bannerFailed.png ? (
          <picture>
            <source srcSet={bannerSrc} type={bannerSrc.endsWith('.gif') ? 'image/gif' : 'image/png'} />
            <img
              src={bannerSrc}
              alt="Profile banner"
              className="w-full h-full object-cover filter blur-md opacity-30"
              onError={() => {
                const isGif = bannerSrc.split('?')[0].endsWith('.gif');
                handleBannerError(isGif ? 'gif' : 'png');
              }}
            />
          </picture>
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-gray-900 to-slate-900 opacity-50">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08)_0%,transparent_100%)] animate-pulse" />
          </div>
        )}
      </div>

      <div className="fixed top-0 w-full z-50">
        <Navbar currentPage="Profile" className="bg-transparent shadow-md shadow-slate-900/50" />
      </div>

      {error && (
        <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-red-900/80 text-red-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-200 hover:text-red-100">
            ✕
          </button>
        </div>
      )}

      {notification && (
        <motion.div
          className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-green-900/80 text-green-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="flex-1">{notification}</span>
          <button onClick={() => setNotification(null)} className="ml-4 text-green-200 hover:text-green-100">
            ✕
          </button>
        </motion.div>
      )}

      {statusConnectionError && (
        <div className="fixed bottom-4 left-4 z-50 bg-yellow-900/80 text-yellow-200 px-3 py-1 rounded-md shadow-lg flex items-center text-xs">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="flex-1">Status updates disconnected</span>
          <button onClick={reconnectStatus} className="ml-2 underline">
            Reconnect
          </button>
        </div>
      )}

      <main className="relative flex-grow pt-20 sm:pt-32 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {loading ? (
          <div className="text-center text-slate-400 text-lg">Loading profile...</div>
        ) : profileData ? (
          <>
            <motion.section
              variants={cardVariants}
              className="bg-black/60 border border-purple-500/20 p-6 sm:p-8 group hover:border-purple-500 transition-colors duration-300 rounded-xl"
            >
              <div className="flex flex-col items-center space-y-6 sm:space-y-0">
                <motion.img
                  src={profileImage}
                  alt="Profile"
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-purple-500/20 group-hover:border-purple-500 transition-colors"
                  whileHover={{ scale: 1.05 }}
                />
                <div className="text-center flex-1 mt-4 space-y-2">
                  <div className="flex flex-col items-center gap-2">
                    {isEditing ? (
                      <input
                        type="text"
                        name="displayName"
                        value={tempProfile.displayName !== undefined ? tempProfile.displayName : tempProfile.username}
                        onChange={handleEditChange}
                        className="text-2xl sm:text-3xl font-bold bg-slate-800 p-2 rounded-md border border-slate-700 focus:ring-2 focus:ring-purple-500 focus:outline-none w-full max-w-md"
                      />
                    ) : (
                      <h1 className="text-2xl sm:text-3xl font-bold text-slate-300">{profileData.displayName || profileData.username}</h1>
                    )}
                    <p className="text-slate-400 text-sm sm:text-base">@{profileData.username}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {discordSocial && (
                        <div className="flex flex-col items-center">
                          <span className="flex items-center text-sm text-indigo-300 bg-indigo-900/50 px-2 py-1 rounded">
                            <img
                              src={getSocialAvatar('discord', discordSocial.socialId, discordSocial, statusData)}
                              alt="Discord avatar"
                              className="w-5 h-5 rounded-full mr-1"
                            />
                            {discordSocial.displayName}
                            <svg className="w-4 h-4 ml-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                          {getSocialStatusIndicator('discord', discordSocial.socialId)}
                        </div>
                      )}
                      {steamSocial && (
                        <div className="flex flex-col items-center">
                          <span className="flex items-center text-sm text-gray-300 bg-gray-800/50 px-2 py-1 rounded">
                            <img
                              src={getSocialAvatar('steam', steamSocial.socialId, steamSocial, statusData)}
                              alt="Steam avatar"
                              className="w-5 h-5 rounded-full mr-1"
                            />
                            {steamSocial.displayName}
                            <svg className="w-4 h-4 ml-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                          {getSocialStatusIndicator('steam', steamSocial.socialId)}
                        </div>
                      )}
                    </div>
                  </div>
                  {isEditing ? (
                    <textarea
                      name="bio"
                      value={tempProfile.bio || ''}
                      onChange={handleEditChange}
                      className="w-full max-w-md bg-slate-800 p-2 rounded-md text-slate-300 border border-slate-700 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none text-sm sm:text-base mx-auto"
                      rows="3"
                    />
                  ) : (
                    <p className="text-slate-300 text-sm sm:text-base max-w-md mx-auto">{profileData.bio || 'No bio yet.'}</p>
                  )}
                  <div className="mt-4 flex flex-wrap justify-center gap-4">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          name="location"
                          value={tempProfile.location || ''}
                          onChange={handleEditChange}
                          placeholder="Location"
                          className="p-2 bg-slate-800 rounded-md text-sm border border-slate-700 focus:ring-2 focus:ring-purple-500 focus:outline-none w-full max-w-xs"
                        />
                        <input
                          type="text"
                          name="website"
                          value={tempProfile.website || ''}
                          onChange={handleEditChange}
                          placeholder="Website"
                          className="p-2 bg-slate-800 rounded-md text-sm border border-slate-700 focus:ring-2 focus:ring-purple-500 focus:outline-none w-full max-w-xs"
                        />
                      </>
                    ) : (
                      <>
                        {profileData.location && <span className="text-slate-400 text-sm sm:text-base">📍 {profileData.location}</span>}
                        {profileData.website && renderWebsite(profileData.website)}
                      </>
                    )}
                  </div>
                  <div className="mt-4">
                    {isEditing ? (
                      <div className="flex flex-wrap justify-center gap-4">
                        <button
                          onClick={saveProfile}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setTempProfile(profileData);
                            setIsEditing(false);
                          }}
                          className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>
                  <div className="mt-4 text-slate-400 text-sm sm:text-base">
                    <p>Joined on {new Date(profileData.createdAt).toLocaleDateString()}</p>
                    <p>Last updated on {new Date(profileData.updatedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </motion.section>

            <div className="sticky top-16 z-10 bg-slate-900/80 backdrop-blur-sm py-4 -mx-4 sm:mx-0 sm:bg-transparent sm:backdrop-blur-none">
              <div className="flex flex-wrap justify-center gap-2 sm:gap-4 border-b border-purple-500/20">
                {tabs.map((tab) => (
                  <motion.button
                    key={tab}
                    onClick={() => setCurrentTab(tab)}
                    className={`px-6 py-2 text-xs sm:text-sm uppercase tracking-wider rounded-md ${
                      currentTab === tab
                        ? 'bg-purple-600 text-white'
                        : 'text-slate-300 hover:bg-purple-500/10'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {tab}
                  </motion.button>
                ))}
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentTab}
                variants={tabContentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {currentTab === 'Profile' && (
                  <motion.div className="grid grid-cols-1 gap-8" variants={cardVariants}>
                    <div className="bg-black/60 border border-purple-500/20 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-slate-300 mb-4">Connected Socials</h3>
                      {profileData.socials && profileData.socials.length > 0 ? (
                        <ul className="space-y-4 text-sm sm:text-base">
                          {profileData.socials.map((social, index) => (
                            <li key={index} className="flex items-center justify-between gap-4">
                              <div className="flex items-center space-x-2 flex-1">
                                <span className="text-purple-500">•</span>
                                <span className="flex items-center gap-2">
                                  {social.platform === 'discord' && (
                                    <img
                                      src={getSocialAvatar(social.platform, social.socialId, social, statusData)}
                                      alt="Discord avatar"
                                      className="w-6 h-6 rounded-full"
                                    />
                                  )}
                                  {social.platform === 'steam' && (
                                    <img
                                      src={getSocialAvatar(social.platform, social.socialId, social, statusData)}
                                      alt="Steam avatar"
                                      className="w-6 h-6 rounded-full"
                                    />
                                  )}
                                  <span className="capitalize">{social.platform}</span>
                                  <span className="text-slate-300">: {social.displayName}</span>
                                  {getSocialStatusIndicator(social.platform, social.socialId)}
                                </span>
                              </div>
                              <button
                                onClick={() => handleUnlinkSocial(social.platform)}
                                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                              >
                                Unlink
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-slate-400">No connected socials.</p>
                      )}
                    </div>
                  </motion.div>
                )}
                {currentTab === 'Stats' && (
                  <motion.div
                    className="bg-black/60 border border-purple-500/20 p-6 rounded-xl"
                    variants={cardVariants}
                  >
                    <h3 className="text-lg font-semibold text-slate-300 mb-4">Profile Stats</h3>
                    <ul className="space-y-4 text-sm sm:text-base">
                      <li className="text-slate-300 flex items-center space-x-2">
                        <span className="text-purple-500">•</span>
                        <span>Joined on {new Date(profileData.createdAt).toLocaleDateString()}</span>
                      </li>
                      <li className="text-slate-300 flex items-center space-x-2">
                        <span className="text-purple-500">•</span>
                        <span>Last updated on {new Date(profileData.updatedAt).toLocaleDateString()}</span>
                      </li>
                    </ul>
                  </motion.div>
                )}
                {currentTab === 'Settings' && (
                  <motion.div
                    className="bg-black/60 border border-purple-500/20 p-6 rounded-xl"
                    variants={cardVariants}
                  >
                    <h3 className="text-lg font-semibold text-slate-300 mb-4">Account Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <button
                          onClick={handleDiscordConnect}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                          disabled={profileData.socials?.some((s) => s.platform === 'discord')}
                        >
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.045-.319 13.579.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 0-.08.013c-.126.09-.252.18-.375.268a.073.073 0 0 0-.029.096 15.153 15.153 0 0 0 4.885 2.354.076.076 0 0 0 .079-.036c.316-.36.635-.74.951-1.138a.08.08 0 0 0 .013-.097 13.3 13.3 0 0 1-1.882-.896.077.077 0 0 0-.077.013c-.124.087-.248.174-.372.261a.074.074 0 0 0-.029.095 14.19 14.19 0 0 0 1.22 1.987.077.077 0 0 0 .084.028 19.917 19.917 0 0 0 6.001-3.03.077.077 0 0 0 .032-.054c.5-4.879-.838-9.422-3.548-13.688a.061.061 0 0 0-.031-.028zM8.02 15.33c-.954 0-1.738-.858-1.738-1.912s.773-1.912 1.738-1.912c.966 0 1.74.858 1.738 1.912s-.777 1.912-1.738 1.912zm7.966 0c-.954 0-1.738-.858-1.738-1.912s.773-1.912 1.738-1.912c.966 0 1.74.858 1.738 1.912s-.777 1.912-1.738 1.912z" />
                          </svg>
                          {profileData.socials?.some((s) => s.platform === 'discord')
                            ? 'Discord Connected'
                            : 'Connect Discord'}
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={handleSteamConnect}
                          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                          disabled={profileData.socials?.some((s) => s.platform === 'steam')}
                        >
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.5 14.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5c.35 0 .68.07 1 .19l1.31-1.31c-.65-.85-1.62-1.38-2.81-1.38-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5c1.19 0 2.16-.53 2.81-1.38l-1.31-1.31c-.32.12-.65.19-1 .19zm6-2.5c0 1.38-1.12 2.5-2.5 2.5s-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5zm-1 0c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5.67 1.5 1.5 1.5 1.5-.67 1.5-1.5z" />
                          </svg>
                          {profileData.socials?.some((s) => s.platform === 'steam')
                            ? 'Steam Connected'
                            : 'Connect Steam'}
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={requestPasswordReset}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm sm:text-base w-full sm:w-auto"
                        >
                          Change Password
                        </button>
                      </div>
                      <div>
                        <button
                          onClick={handleLogout}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm sm:text-base w-full sm:w-auto"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
                {currentTab === 'Social' && (
                  <motion.div
                    className="bg-black/60 border border-purple-500/20 p-6 rounded-xl"
                    variants={cardVariants}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-300">Live Social Status</h3>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex h-2.5 w-2.5 rounded-full ${isStatusConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                        <span className="text-sm text-slate-400">
                          {isStatusConnecting
                            ? 'Connecting...'
                            : isStatusConnected
                              ? 'Connected'
                              : 'Disconnected'}
                        </span>
                        {!isStatusConnected && !isStatusConnecting && (
                          <button
                            onClick={reconnectStatus}
                            className="text-xs text-purple-400 hover:text-purple-300 underline"
                          >
                            Reconnect
                          </button>
                        )}
                      </div>
                    </div>
                    {Object.keys(statusData).length > 0 ? (
                      <div className="space-y-6">
                        {Object.entries(statusData).map(([platform, users]) => (
                          <div key={platform} className="space-y-4">
                            <h4 className="text-md font-medium text-purple-300 capitalize">{platform}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {Object.entries(users).map(([userId, data]) => {
                                const social = profileData.socials?.find(
                                  s => s.platform === platform && s.socialId === userId
                                );
                                if (!social) return null;
                                return (
                                  <div
                                    key={userId}
                                    className="bg-slate-800/50 rounded-lg p-3 border border-purple-500/10 hover:border-purple-500/30 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      {platform === 'discord' && (
                                        <img
                                          src={getSocialAvatar(platform, userId, social, statusData)}
                                          alt="Discord avatar"
                                          className="w-10 h-10 rounded-full"
                                        />
                                      )}
                                      {platform === 'steam' && (
                                        <img
                                          src={getSocialAvatar(platform, userId, social, statusData)}
                                          alt="Steam avatar"
                                          className="w-10 h-10 rounded-full"
                                        />
                                      )}
                                      <div>
                                        <p className="text-slate-200 font-medium">{social.displayName}</p>
                                        <div className="text-sm">
                                          {platform === 'discord' && (
                                            <div className="flex items-center gap-1">
                                              <span className={`w-2 h-2 rounded-full ${
                                                data.status === 'online' ? 'bg-green-500' :
                                                data.status === 'idle' ? 'bg-yellow-500' :
                                                data.status === 'dnd' ? 'bg-red-500' : 'bg-gray-500'
                                              }`}></span>
                                              <span className="text-slate-300">
                                                {data.status === 'online' ? 'Online' :
                                                data.status === 'idle' ? 'Idle' :
                                                data.status === 'dnd' ? 'Do Not Disturb' : 'Offline'}
                                              </span>
                                            </div>
                                          )}
                                          {platform === 'steam' && (
                                            <div className="flex items-center gap-1">
                                              <span className={`w-2 h-2 rounded-full ${
                                                data.online ? 'bg-green-500' : 'bg-gray-500'
                                              }`}></span>
                                              <span className="text-slate-300">
                                                {data.online ? 'Online' : 'Offline'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    {platform === 'discord' && data.game && (
                                      <div className="mt-3 ml-2 text-sm text-slate-300">
                                        <div className="flex items-center gap-2">
                                          <span className="text-purple-300">🎮</span>
                                          <span>Playing {data.game}</span>
                                        </div>
                                      </div>
                                    )}
                                    {platform === 'steam' && data.ingame && (
                                      <div className="mt-3 ml-2 text-sm text-slate-300">
                                        <div className="flex items-center gap-2">
                                          <span className="text-purple-300">🎮</span>
                                          <span>Playing {data.game}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        {Object.keys(socialSubscriptions || {}).length === 0 ? (
                          <p>No connected social accounts to monitor.</p>
                        ) : isStatusConnecting ? (
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 border-t-2 border-b-2 border-purple-500 rounded-full animate-spin mb-3"></div>
                            <p>Connecting to status service...</p>
                          </div>
                        ) : (
                          <p>No status updates available yet.</p>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          <div className="text-center text-slate-400 text-lg">Failed to load profile</div>
        )}
      </main>

      <footer className="relative bg-slate-900/80 text-slate-400 py-12 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Mediocre Makers</h4>
            </div>
           {/* <div>
              <h3 className="text-purple-300 font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="https://4fingerstudios.com" className="hover:text-purple-300 transition-colors duration-300">
                    Home
                  </a>
                </li>
                <li>
                  <a href="https://4fingerstudios.com/contact" className="hover:text-purple-300 transition-colors duration-300">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="https://4fingerstudios.com/Privacy" className="hover:text-purple-300 transition-colors duration-300">
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-purple-300 font-bold mb-4">Contact Us</h3>
              <p className="text-sm mb-2">
                <span className="text-purple-300 mr-2">Email:</span> 4.finger.studios.com@gmail.com
              </p>
              <p className="text-sm">
                <span className="text-purple-300 mr-2">Support:</span> support@4fingerstudios.com
              </p>
            </div>*/}
          </div> 
          <div className="mt-12 pt-6 border-t border-slate-800/50 text-center text-xs">
            <p>© {new Date().getFullYear()} The Mediocre Makers & 4FingerStudios.. All rights reserved.</p>
          </div>
        </div> 
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
      </footer>
      <ConfirmationPopup
        isOpen={confirmationPopup.isOpen}
        onClose={closeConfirmationPopup}
        onConfirm={confirmationPopup.confirmAction}
        title={confirmationPopup.title}
        message={confirmationPopup.message}
        icon={confirmationPopup.icon}
        confirmText="Yes, Unlink"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        cancelButtonClass="bg-gray-700 hover:bg-gray-600"
      />
    </motion.div>
  );
};

export default UserProfile;